import { useState, useEffect } from 'react';
import { SettingCard, SettingField, SettingActions, SettingAddButton, useSettingEditing } from './SettingControls';
import butterflyApi from "../../ButterflyApi";
import FileUpload from "../../Utils/FileUpload";
import Utils from "../../utils";


const NO_NAME_RELATIONS = ['엄마', '아빠', '할머니', '할아버지']
const relationNeedsName = relation => !!relation && !NO_NAME_RELATIONS.includes(relation)
const FAMILY_GROUP_NO = 1
const FAMILY_IMAGE_FEATURE_NO = 15
const FAMILY_FIRSTNAME_FEATURE_NO = 5
const FAMILY_LASTNAME_FEATURE_NO = 12
const FAMILY_RELATION_FEATURE_NO = 9

const Family = ({ features, heroNo, onDelete, index, setLoading, reloading, setLoadingMessage, editing, setEditing }) => {

  const [ordering, setOrdering] = useState()
  const [firstName, setFirstName] = useState('')
  const [firstNameFeatureContentNo, setFirstNameFeatureContentNo] = useState(0)
  const [lastName, setLastName] = useState('')
  const [lastNameFeatureContentNo, setLastNameFeatureContentNo] = useState(0)
  const [relation, setRelation] = useState('')
  const [relationFeatureContentNo, setRelationFeatureContentNo] = useState(0)
  const [imageUrl, setImageUrl] = useState('')
  const [imageUrlFeatureContentNo, setImageUrlFeatureContentNo] = useState(0)
  const [originFeature, setOriginFeature] = useState()
  const [updated, setUpdated] = useState(false)
  const [graphicImageUrl, setGraphicImageUrl] = useState('')

  const requiresName = relationNeedsName(relation)

  useEffect(() => {
    if (!features) {
      setOriginFeature({
        firstName: '',
        lastName: '',
        relation: '',
        imageUrl: '',
        graphicImageUrl: '',
      })
      return
    }
    const imageFeature = features?.filter(feature => feature.heroFeatureNo == FAMILY_IMAGE_FEATURE_NO)[0];
    const firstNameFeature = features?.filter(feature => feature.heroFeatureNo == FAMILY_FIRSTNAME_FEATURE_NO)[0];
    const lastNameFeature = features?.filter(feature => feature.heroFeatureNo == FAMILY_LASTNAME_FEATURE_NO)[0];
    const relationFeature = features?.filter(feature => feature.heroFeatureNo == FAMILY_RELATION_FEATURE_NO)[0];
    setOrdering(features?.[0].ordering)
    setFirstName(firstNameFeature?.answer || '')
    setFirstNameFeatureContentNo(firstNameFeature?.heroFeatureContentNo)
    setLastName(lastNameFeature?.answer || '')
    setLastNameFeatureContentNo(lastNameFeature?.heroFeatureContentNo)
    setRelation(relationFeature?.answer || '')
    setRelationFeatureContentNo(relationFeature?.heroFeatureContentNo)
    setImageUrl(imageFeature?.answer || '')
    setImageUrlFeatureContentNo(imageFeature?.heroFeatureContentNo)
    setGraphicImageUrl(imageFeature?.graphicImageUrl || '')
    setOriginFeature({
      firstName: firstNameFeature?.answer || '',
      lastName: lastNameFeature?.answer || '',
      relation: relationFeature?.answer || '',
      imageUrl: imageFeature?.answer || '',
      graphicImageUrl: imageFeature?.graphicImageUrl || '',
    })
    setUpdated(false)
  }, [features])

  useEffect(() => {
    if (!originFeature) {
      setUpdated(false)
      return
    }
    if (firstName != originFeature.firstName) {
      setUpdated(true)
      return
    }
    if (lastName != originFeature.lastName) {
      setUpdated(true)
      return
    }
    if (relation != originFeature.relation) {
      setUpdated(true)
      return
    }
    if (imageUrl != originFeature.imageUrl) {
      setUpdated(true)
      return
    }
    setUpdated(false)
  }, [firstName, lastName, relation, imageUrl, originFeature])

  useSettingEditing(updated, setEditing)

  const _onDelete = async () => {
    if (firstName || lastName || relation || imageUrl) {
      if (!window.confirm("입력된 내용이 있습니다. 그래도 삭제하시겠나요?")) {
        return
      }
    }
    if (index > 0) {
      onDelete(index)
      return
    }
    setLoading(true)
    const featureContentNos = [firstNameFeatureContentNo, lastNameFeatureContentNo, relationFeatureContentNo, imageUrlFeatureContentNo]
    await butterflyApi.deleteHeroFeatures(heroNo, featureContentNos.filter(v => v).join(','))
    setLoading(false)
    reloading()

  }

  const onSave = async () => {
    if (!relation) {
      alert('관계를 선택해 주세요.')
      return
    }
    if (requiresName && !firstName) {
      alert('이름을 입력해 주세요.')
      return
    }
    const models = []
    if (requiresName && firstName) {
      models.push({
        heroFeatureContentNo: firstNameFeatureContentNo,
        answer: firstName,
        ordering: ordering || index,
        heroFeatureNo: FAMILY_FIRSTNAME_FEATURE_NO
      })
    }
    if (requiresName && lastName) {
      models.push({
        heroFeatureContentNo: lastNameFeatureContentNo,
        answer: lastName,
        ordering: ordering || index,
        heroFeatureNo: FAMILY_LASTNAME_FEATURE_NO
      })
    }
    if (relation) {
      models.push({
        heroFeatureContentNo: relationFeatureContentNo,
        answer: relation,
        ordering: ordering || index,
        heroFeatureNo: FAMILY_RELATION_FEATURE_NO
      })
    }
    if (imageUrl) {
      setLoadingMessage('이미지 처리 중에는\n 시간이 오래 걸릴 수 있습니다.\n잠시만 기다려 주세요.')
      models.push({
        heroFeatureContentNo: imageUrlFeatureContentNo,
        answer: imageUrl,
        ordering: ordering || index,
        heroFeatureNo: FAMILY_IMAGE_FEATURE_NO
      })
    }
    setLoading(true)
    if (!requiresName) {
      const featureContentNos = [firstNameFeatureContentNo, lastNameFeatureContentNo].filter(v => v)
      if (featureContentNos.length > 0) {
        await butterflyApi.deleteHeroFeatures(heroNo, featureContentNos.join(','))
      }
    }
    await butterflyApi.updateHeroFeatures(heroNo, models)
    setLoading(false)
    setUpdated(false)
    setLoadingMessage('')
    reloading()
  }

  const onCancel = () => {
    setFirstName(originFeature.firstName)
    setLastName(originFeature.lastName)
    setRelation(originFeature.relation)
    setImageUrl(originFeature.imageUrl)
    setGraphicImageUrl(originFeature.graphicImageUrl)
  }

  const onImageUpload = async (fileInfo) => {
    const url = fileInfo.operationUrls?.[0]?.replace("http://", "https://") || fileInfo.url.replace("http://", "https://")
    setLoadingMessage('이미지 처리 중에는\n 시간이 오래 걸릴 수 있습니다.\n잠시만 기다려 주세요.')
    setLoading(true)
    setTimeout(() => {
      setImageUrl(url)
      setGraphicImageUrl(url)
      setUpdated(true)
      setLoading(false)
      setLoadingMessage('')
    }, 5000)

  }

  const onRelationChange = (relation) => {
    setRelation(relation)
    if (!relationNeedsName(relation)) {
      setFirstName('')
      setLastName('')
    }
  }


  return <SettingCard label={relation || '새 가족'} updated={updated} disabled={editing && !updated} onDelete={_onDelete}>
    <div className="setting-photo">
      <img src={graphicImageUrl || imageUrl || '/image/no_profile_image.png'} alt={`${relation || '가족'} 프로필`} />
      <div className="setting-photo-copy">
        <p>가족의 얼굴도 동화에 담아요.<br />독사진 · 최대 10MB</p>
        <FileUpload path="/public/images/temp" filename={Utils.uuidv4()} operations="300" uploaded={onImageUpload} onlyImage={true} setLoading={setLoading} maxSize={10000000} overSizeMessage="10MB 이하의 이미지만 업로드 가능합니다.">
          <button type="button" className="setting-photo-button"><img src="/image/edit.svg" alt="" />{imageUrl ? '사진 변경' : '사진 등록'}</button>
        </FileUpload>
      </div>
    </div>
    <SettingField label="아이와의 관계" as="select" value={relation} onChange={e => onRelationChange(e.target.value)}>
      <option value="">관계를 선택해 주세요</option>
      {['엄마', '아빠', '형', '오빠', '누나', '언니', '남동생', '여동생', '할머니', '할아버지'].map(item => <option key={item} value={item}>{item}</option>)}
    </SettingField>
    {requiresName && <div className="setting-fields">
      <SettingField label="성" value={lastName} placeholder="김" onChange={e => setLastName(e.target.value)} />
      <SettingField label="이름" value={firstName} placeholder="가족의 이름" onChange={e => setFirstName(e.target.value)} />
    </div>}
    {updated && <SettingActions onSave={onSave} onCancel={onCancel} />}
  </SettingCard>
}

const FamilySetting = ({ heroNo, setLoading, setLoadingMessage, editing, setEditing }) => {

  const [familyFeaturesByOrder, setFamilyFeaturesByOrder] = useState()
  const [addFamily, setAddFamily] = useState([])

  const load = async () => {
    if (!heroNo) return
    const data = await butterflyApi.getHeroFeatures(heroNo)
    const familyFeatures = data.items.filter(item => item.groupNo == FAMILY_GROUP_NO)
    const _familyFeaturesByOrder = Object.groupBy(familyFeatures, (item) => item.ordering)
    setFamilyFeaturesByOrder(_familyFeaturesByOrder)
  }

  useEffect(() => {
    load();
  }, [heroNo])

  const maxOrdering = (familyFeaturesByOrder && Object.keys(familyFeaturesByOrder).length > 0) ? Math.max(...Object.keys(familyFeaturesByOrder)) : 0

  return <div className="setting-stack">
    {familyFeaturesByOrder && Object.keys(familyFeaturesByOrder).length === 0 && addFamily.length === 0 && <p className="settings-empty">함께 이야기 속으로 떠날 가족을 추가해 주세요.</p>}
    {
      familyFeaturesByOrder && Object.keys(familyFeaturesByOrder).map(key => {
        const features = familyFeaturesByOrder[key]
        return <Family key={features[0].ordering} heroNo={heroNo} setLoading={setLoading} setLoadingMessage={setLoadingMessage} features={features}
          editing={editing} setEditing={setEditing}
          reloading={() => {
            load()
          }} />
      })
    }
    {
      addFamily.map((i) => {
        return <Family key={i} index={i} heroNo={heroNo} setLoading={setLoading} setLoadingMessage={setLoadingMessage}
          editing={editing} setEditing={setEditing}
          reloading={() => {
            const _addFamily = addFamily.filter(el => el != i)
            setAddFamily(_addFamily)
            load()
          }}
          onDelete={(i) => {
            const _addFamily = addFamily.filter(el => el != i)
            setAddFamily(_addFamily)
          }} />
      })

    }
    <SettingAddButton disabled={editing} onClick={() => {
      const max = Math.max(maxOrdering, ...addFamily)
      setAddFamily(current => current.concat(max + 1))
    }}>가족 추가하기</SettingAddButton>
  </div>
}

export default FamilySetting
